import { NOTICE_TYPE_INSTRUCTOR_BAR, NOTICE_TYPE_UFB_SMART_BAR } from './bar-types';
/**
 * Fallback notices to show when the backend returns 204 (no notices available).
 * These provide a consistent user experience with relevant messaging based on user state.
 */

/**
 * Creates fallback notice for logged-out non-subscribers
 * @param gettext Internationalization function for translating text
 * @returns Fallback notice with translated copy
 */
export const createFallbackNoticeLoggedOut = gettext => ({
  data: {
    id: -1,
    action_url: 'https://business.udemy.com/spotlight/ai-upskilling',
    mobile_deeplink_url: '',
    days_to_show_timer: 0,
    enable_timer: false,
    enable_seconds_in_timer: false,
    icon: null,
    subtitle: gettext('| Get the skills with Udemy Business.'),
    title: gettext('AI is changing the game'),
    mobile_title: gettext('AI is changing the game'),
    cta_text: '',
    cta_applied_text: '',
    enable_clp_sticky: false,
    clp_sticky_reset_timer_in_minutes: '5',
    theme: 'yellow',
    personalized_notice_set_id: null,
    personalized_notice_set_name: null,
    target_group_id: -1
  },
  membership: {
    use_case: 'ad_hoc_deal_visitors',
    end_time: null,
    opt_in: true // Set to true to prevent opt-in CTA button from rendering for fallback notices.
  }
});

/**
 * Creates fallback notice for logged-in non-subscribers
 * @param gettext Internationalization function for translating text
 * @returns Fallback notice with translated copy
 */
export const createFallbackNoticeLoggedIn = gettext => ({
  data: {
    id: -1,
    // Unique ID for fallback notice
    action_url: '/personal-plan/',
    mobile_deeplink_url: '',
    days_to_show_timer: 0,
    enable_timer: false,
    enable_seconds_in_timer: false,
    icon: null,
    subtitle: gettext('| Accelerate your career with access to 26,000+ of our top courses. Try it today.'),
    title: gettext('Get started with Personal Plan'),
    mobile_title: gettext('Get started with Personal Plan'),
    cta_text: '',
    cta_applied_text: '',
    enable_clp_sticky: false,
    clp_sticky_reset_timer_in_minutes: '5',
    theme: 'yellow',
    personalized_notice_set_id: null,
    personalized_notice_set_name: null,
    target_group_id: -1
  },
  membership: {
    use_case: 'default_logged_in',
    end_time: null,
    opt_in: true // Set to true to prevent opt-in CTA button from rendering for fallback notices
  }
});

/**
 * Creates fallback notice for Personal Plan subscribers
 * @param gettext Internationalization function for translating text
 * @returns Fallback notice with translated copy
 */
export const createFallbackNoticeSubscriber = gettext => ({
  data: {
    id: -1,
    // Unique ID for fallback notice
    action_url: '/home/my-courses/learning/',
    mobile_deeplink_url: '',
    days_to_show_timer: 3,
    enable_timer: false,
    enable_seconds_in_timer: false,
    icon: null,
    subtitle: gettext('| What skill will you learn next? Explore now.'),
    title: gettext('Your Personal Plan includes 26,000+ courses in AI, tech, business, creativity and more'),
    mobile_title: gettext('Your Personal Plan includes 26,000+ courses in AI, tech, business, creativity and more'),
    cta_text: '',
    cta_applied_text: '',
    enable_clp_sticky: false,
    clp_sticky_reset_timer_in_minutes: '0',
    theme: 'yellow',
    personalized_notice_set_id: null,
    personalized_notice_set_name: null,
    target_group_id: -1
  },
  membership: {
    use_case: 'personal_plan_subscriber',
    end_time: null,
    opt_in: true // Set to true to prevent opt-in CTA button from rendering for fallback notices
  }
});

/**
 * Get the appropriate fallback notice based on user authentication and subscription status
 * @param gettext Internationalization function for translating text
 * @param isAuthenticated Whether the user is logged in
 * @param isPersonalPlanSubscriber Whether the user is a Personal Plan subscriber
 * @param noticeTypeName The name of the notice type (e.g., NOTICE_TYPE_INSTRUCTOR_BAR, NOTICE_TYPE_SMART_BAR, NOTICE_TYPE_UFB_SMART_BAR)
 * @returns The appropriate fallback notice with translated copy, or null if fallback should not be shown
 */
export function getFallbackNotice(gettext, isAuthenticated, isPersonalPlanSubscriber, noticeTypeName) {
  // Never show fallback notices for instructor_bar or ufb_smart_bar use cases
  if (noticeTypeName === NOTICE_TYPE_INSTRUCTOR_BAR || noticeTypeName === NOTICE_TYPE_UFB_SMART_BAR) {
    return null;
  }
  if (isPersonalPlanSubscriber) {
    return createFallbackNoticeSubscriber(gettext);
  }
  if (isAuthenticated) {
    return createFallbackNoticeLoggedIn(gettext);
  }
  return createFallbackNoticeLoggedOut(gettext);
}
//# sourceMappingURL=fallback-notices.js.map